// ─── Feuille : ÉDITION D'UN COURS de l'emploi du temps ────────────
// Matière + salle + couleur + catégorie + heures DÉBUT/FIN libres via le
// sélecteur d'heure (comme un événement) : le cours peut remplir une FRACTION
// de créneau (fin 13:30 sur des créneaux d'1 h = un demi-créneau). Le nombre
// de créneaux est DÉRIVÉ des heures (stepper = ± une durée de créneau).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, SelecteurHeure, Stepper, ChampBouton, stylesForm, useUI, useT, HIT } from '@mop/core';
import { COULEURS_EDT } from './services/edt';
import { CATEGORIES_EVT, emojiCategorie, labelCategorie } from './data/categories';
import FeuilleChoixLieu from './FeuilleChoixLieu';

const _h = (x) => { const [h, m] = String(x || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const _hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;

export default function FeuilleCaseEDT({ visible, info, creneaux, lieux = [], onEnregistrer, onSupprimer, onCopier, onFermer }) {
  // info = { jourIdx, creneauIdx, case: { nom?, matiere, salle, couleur, categorie?, heure?, heureFin?, lieuId?, lieu?, span? } | null }
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [nom, setNom] = useState('');
  const [matiere, setMatiere] = useState('');
  const [salle, setSalle] = useState('');
  const [couleur, setCouleur] = useState(COULEURS_EDT[0]);
  const [categorie, setCategorie] = useState('');
  const [lieuId, setLieuId] = useState('');
  const [lieuNom, setLieuNom] = useState('');
  const [choixLieu, setChoixLieu] = useState(false);
  const [hDeb, setHDeb] = useState('08:00');
  const [hFin, setHFin] = useState('09:00');
  const [pickerH, setPickerH] = useState(null);   // 'debut' | 'fin'

  useEffect(() => {
    if (!visible) return;
    const c = info?.case || {};
    const ci = info?.creneauIdx ?? 0;
    const span = Math.max(1, c.span || 1);
    setNom(c.nom || ''); setMatiere(c.matiere || ''); setSalle(c.salle || '');
    setCouleur(c.couleur || COULEURS_EDT[0]); setCategorie(c.categorie || '');
    setLieuId(c.lieuId || ''); setLieuNom(c.lieu || '');
    setHDeb(c.heure || creneaux[ci]?.debut || '08:00');
    setHFin(c.heureFin || creneaux[Math.min(ci + span - 1, creneaux.length - 1)]?.fin || '09:00');
    setPickerH(null); setChoixLieu(false);
  }, [visible, info]);

  if (!info) return null;
  // Bornes de la grille + durée d'un créneau (pas du stepper).
  const grilleDeb = _h(creneaux[0]?.debut || '08:00');
  const grilleFin = _h(creneaux[creneaux.length - 1]?.fin || '17:00');
  const dur = Math.max(5, _h(creneaux[0]?.fin || '09:00') - grilleDeb);
  const md = _h(hDeb), mf = _h(hFin);
  const heuresOk = mf > md;
  // Nombre de créneaux DÉRIVÉ des heures (fractions possibles : 0,5 / 1,25…).
  const nb = Math.round(((mf - md) / dur) * 100) / 100;
  const c0 = info.case || {};
  const ci0 = info.creneauIdx ?? 0;
  const hDeb0 = c0.heure || creneaux[ci0]?.debut || '';
  const hFin0 = c0.heureFin || creneaux[Math.min(ci0 + Math.max(1, c0.span || 1) - 1, creneaux.length - 1)]?.fin || '';
  const modifie = (!!nom.trim() || !!matiere.trim()) && heuresOk && (matiere.trim() !== (c0.matiere || '') || salle.trim() !== (c0.salle || '')
    || nom.trim() !== (c0.nom || '') || lieuId !== (c0.lieuId || '')
    || couleur !== (c0.couleur || COULEURS_EDT[0]) || categorie !== (c0.categorie || '')
    || hDeb !== hDeb0 || hFin !== hFin0);

  return (
    <>
    <Feuille visible={visible} titre={t('edt.caseTitre')} emoji="🕐" hauteur={0.78} onFermer={onFermer}
      action={{ icone: info.case ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !modifie,
        onPress: () => onEnregistrer({ nom: nom.trim(), matiere: matiere.trim(), salle: salle.trim(), couleur, categorie, heure: hDeb, heureFin: hFin, lieuId, lieu: lieuNom }) }}
      boutons={info.case ? (
        <>
          <TouchableOpacity hitSlop={HIT} onPress={() => onCopier?.(info.case)} accessibilityLabel={t('edt.copier')}>
            <Text style={{ fontSize: 17 }}>📋</Text>
          </TouchableOpacity>
          <TouchableOpacity hitSlop={HIT} onPress={onSupprimer} accessibilityLabel={t('commun.supprimer')}>
            <Text style={{ fontSize: 17 }}>🗑️</Text>
          </TouchableOpacity>
        </>
      ) : undefined}>

      {/* NOM : champ PRINCIPAL du créneau (matière et salle sont optionnelles). */}
      <Text style={s.lbl}>{t('edt.nomCreneau')}</Text>
      <TextInput style={s.input} value={nom} onChangeText={setNom} autoFocus={!info.case}
        placeholder={t('edt.phNomCreneau')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('edt.matiereOptionnel')}</Text>
      <TextInput style={s.input} value={matiere} onChangeText={setMatiere}
        placeholder={t('edt.phMatiere')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('edt.salleOptionnel')}</Text>
      <TextInput style={s.input} value={salle} onChangeText={setSalle}
        placeholder={t('edt.phSalle')} placeholderTextColor={UI.faint} />

      {/* Emplacement : un LIEU du calendrier (géré via menu → Lieux). */}
      <Text style={s.lbl}>{t('edt.emplacement')}</Text>
      <ChampBouton icone="📍" valeur={lieuNom} placeholder={t('lieux.aucun')} onPress={() => setChoixLieu(true)} />

      <Text style={s.lbl}>{t('edt.couleur')}</Text>
      <View style={s.puces}>
        {COULEURS_EDT.map(c => (
          <TouchableOpacity key={c} onPress={() => setCouleur(c)}
            style={[s.pastille, { backgroundColor: c }, couleur === c && s.pastilleOn]} />
        ))}
      </View>

      {/* Catégorie du cours — mêmes puces que le formulaire d'événement. */}
      <Text style={s.lbl}>{t('calendrier.categorieOptionnel')}</Text>
      <View style={s.puces}>
        <TouchableOpacity style={[s.catChip, !categorie && s.puceOn]} onPress={() => setCategorie('')} accessibilityLabel={t('calendrier.aucuneCategorie')}>
          <Text style={[s.catTxt, { color: UI.muted }]}>–</Text>
        </TouchableOpacity>
        {CATEGORIES_EVT.map(cat => (
          <TouchableOpacity key={cat.id} style={[s.catChip, categorie === cat.id && s.puceOn]}
            onPress={() => setCategorie(cat.id)} accessibilityLabel={labelCategorie(cat.id, t)}>
            <Text style={s.catTxt}>{emojiCategorie(cat.id)}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Heures DÉBUT/FIN : prioritaires, LIBRES (fractions de créneau OK). */}
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('calendrier.heureDebut')}</Text>
          <ChampBouton icone="🕒" valeur={hDeb} onPress={() => setPickerH('debut')} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[s.lbl, !heuresOk && { color: UI.danger }]}>{t('calendrier.heureFin')}</Text>
          <ChampBouton icone="🕒" valeur={hFin} enErreur={!heuresOk} onPress={() => setPickerH('fin')} />
        </View>
      </View>

      {/* Nombre de créneaux : DÉRIVÉ des heures ; le stepper ajuste la FIN
          d'une durée de créneau (borné à la grille). */}
      <Text style={s.lbl}>{t('edt.nbCreneaux')}</Text>
      <Stepper valeur={String(nb).replace('.', ',')}
        onMoins={() => setHFin(_hhmm(Math.max(md + 5, mf - dur)))}
        onPlus={() => setHFin(_hhmm(Math.min(grilleFin, mf + dur)))}
        apres={<Text style={s.finAuto}>{hDeb} → {hFin}</Text>} />
    </Feuille>

    {/* Choix du lieu — modale SŒUR (pas d'imbrication native). */}
    <FeuilleChoixLieu visible={choixLieu} lieux={lieux} valeurId={lieuId}
      onSelect={(l) => { setLieuId(l?.id || ''); setLieuNom(l?.nom || ''); }}
      onFermer={() => setChoixLieu(false)} />

    {/* Sélecteur d'heure — modale SŒUR (pas d'imbrication native). */}
    <SelecteurHeure visible={!!pickerH}
      titre={pickerH === 'fin' ? t('calendrier.heureFin') : t('calendrier.heureDebut')}
      valeur={pickerH === 'fin' ? hFin : hDeb}
      onChange={(hhmm) => { if (pickerH === 'fin') setHFin(hhmm); else setHDeb(hhmm); }}
      onFermer={() => setPickerH(null)} />
    </>
  );
}

// Styles de formulaire : base partagée du Core + styles propres à la feuille.
const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  pastille: { width: 30, height: 30, borderRadius: 15 },
  pastilleOn: { borderWidth: 3, borderColor: UI.ink },
  puceOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  catChip: { width: 40, height: 40, borderRadius: 11, borderWidth: 1, borderColor: UI.champBord, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  catTxt: { fontSize: 18 },
  finAuto: { fontSize: 12.5, color: UI.muted, fontWeight: '600', marginLeft: 6 },
});
